/* ============================================================
   Classy Kitchen — Premium Product Page
   assets/premium-product.js
   Vanilla JS only. No jQuery. No frameworks.
   ============================================================ */

(function () {
  'use strict';

  /* ---------- Boot once per section instance ---------- */
  document.querySelectorAll('.ck-product').forEach(initProductSection);

  function initProductSection(root) {
    if (root.dataset.ckInit) return;
    root.dataset.ckInit = 'true';

    let productData = {};
    try {
      productData = JSON.parse(root.dataset.productJson || '{}');
    } catch (e) {
      productData = {};
    }

    initGallery(root);
    initVariantMediaMap(root);
    initWishlist(root);
    initVariantPicker(root, productData);
    initVariantCards(root, productData);
    initQuantityStepper(root);
    initAddToCart(root);
    initStickyBar(root);
    initTabs(root);
    initMobileAccordion(root);
    initFaq(root);
    initPincodeChecker(root);
    initRipple(root);
    initFadeInObserver(root);
    initRelatedSliders(root);
    initRecentlyViewed(root, productData);
    initFrequentlyBoughtTogether(root);
  }

  /* ============================================================
     GALLERY: thumbnails, prev/next, counter, zoom, lazy load
     ============================================================ */
  function initGallery(root) {
    const stage = root.querySelector('[data-ck-stage]');
    if (!stage) return;

    const slides = Array.from(stage.querySelectorAll('[data-ck-slide]'));
    const thumbs = Array.from(root.querySelectorAll('[data-ck-thumb]'));
    const prevBtn = root.querySelector('[data-ck-prev]');
    const nextBtn = root.querySelector('[data-ck-next]');
    const currentEl = root.querySelector('[data-ck-current]');
    let index = 0;

    function show(i) {
      if (i < 0) i = slides.length - 1;
      if (i >= slides.length) i = 0;
      index = i;

      slides.forEach((s, idx) => s.classList.toggle('is-active', idx === index));
      thumbs.forEach((t, idx) => {
        t.classList.toggle('is-active', idx === index);
        t.setAttribute('aria-selected', idx === index ? 'true' : 'false');
      });
      if (currentEl) currentEl.textContent = index + 1;

      // pause any videos not visible
      slides.forEach((s, idx) => {
        const video = s.querySelector('video');
        if (video && idx !== index) video.pause();
      });
    }

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => show(parseInt(thumb.dataset.index, 10)));
    });
    if (prevBtn) prevBtn.addEventListener('click', () => show(index - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => show(index + 1));

    // keyboard support
    stage.setAttribute('tabindex', '0');
    stage.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') show(index - 1);
      if (e.key === 'ArrowRight') show(index + 1);
    });

    // swipe support
    let touchStartX = 0;
    stage.addEventListener('touchstart', (e) => { touchStartX = e.touches[0].clientX; }, { passive: true });
    stage.addEventListener('touchend', (e) => {
      const diff = e.changedTouches[0].clientX - touchStartX;
      if (Math.abs(diff) > 40) show(diff > 0 ? index - 1 : index + 1);
    }, { passive: true });

    // hover zoom (desktop): track pointer position for a focal zoom
    stage.querySelectorAll('[data-ck-zoom]').forEach((zoomEl) => {
      const img = zoomEl.querySelector('img');
      if (!img) return;
      zoomEl.addEventListener('mousemove', (e) => {
        const rect = zoomEl.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        img.style.transformOrigin = x + '% ' + y + '%';
      });
      zoomEl.addEventListener('mouseleave', () => {
        img.style.transformOrigin = 'center center';
      });
    });

    // Expose a way for the variant picker / variant cards to switch
    // the gallery to whichever slide matches a variant's own image,
    // without duplicating the show()/thumb-sync logic here. Tries
    // the media id first — this is now built from
    // variant.featured_media.id (the same id space product.media
    // uses, matching Shopify's own Dawn/Horizon gallery approach) —
    // then falls back to a filename match for extra safety.
    root._ckGallerySlideToMedia = function (mediaId, mediaFilename) {
      let target = -1;
      if (mediaId) {
        target = slides.findIndex((s) => String(s.dataset.mediaId) === String(mediaId));
      }
      if (target === -1 && mediaFilename) {
        target = slides.findIndex((s) => s.dataset.mediaFilename && s.dataset.mediaFilename === mediaFilename);
      }
      if (target > -1) {
        show(target);
      } else if (window.console && console.warn) {
        // Diagnostic only — never blocks the UI. If this fires, the
        // variant's image genuinely isn't present among the gallery
        // slides (or isn't assigned in Shopify Admin under
        // Variants → [variant] → Image), so no JS matching strategy
        // can find it. Check console for the values that failed to
        // match against each slide's data-media-filename/data-media-id.
        console.warn('[CK gallery] No slide matched variant image.', { mediaId, mediaFilename, availableSlides: slides.map((s) => ({ id: s.dataset.mediaId, filename: s.dataset.mediaFilename })) });
      }
    };
  }

  /* ============================================================
     VARIANT → MEDIA MAP — parses the Liquid-built JSON map
     (data-ck-variant-media-map) into root._ckVariantMediaMap.
     This is the reliable source for "which gallery image belongs
     to this variant", since it's built directly from Liquid
     variant objects rather than the nested product|json blob,
     which can drop variant.featured_image on deep serialization.
     ============================================================ */
  function initVariantMediaMap(root) {
    root._ckVariantMediaMap = {};
    const mapScript = root.querySelector('[data-ck-variant-media-map]');
    if (!mapScript) return;
    try {
      root._ckVariantMediaMap = JSON.parse(mapScript.textContent.trim() || '{}');
    } catch (e) {
      root._ckVariantMediaMap = {};
    }
  }

  /* ============================================================
     WISHLIST
     ============================================================ */
  function initWishlist(root) {
    const btn = root.querySelector('[data-ck-wishlist]');
    if (!btn) return;
    const handle = root.dataset.productHandle;
    const key = 'ck_wishlist';

    function getWishlist() {
      try { return JSON.parse(localStorage.getItem(key) || '[]'); }
      catch (e) { return []; }
    }
    function setActive() {
      const list = getWishlist();
      btn.classList.toggle('is-active', list.includes(handle));
    }
    setActive();

    btn.addEventListener('click', () => {
      let list = getWishlist();
      if (list.includes(handle)) {
        list = list.filter((h) => h !== handle);
      } else {
        list.push(handle);
      }
      try { localStorage.setItem(key, JSON.stringify(list)); } catch (e) { /* storage unavailable */ }
      setActive();
    });
  }

  /* ============================================================
     MONEY FORMAT + VARIANT UI SYNC — shared by the pill picker
     and the variant cards, so both apply the same full update
     (price, compare price, discount %, stock, button state,
     and gallery image) instead of only touching the price text.
     ============================================================ */
  function formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return '₹' + amount.replace(/\.00$/, '');
  }

  function applyVariantToUI(root, variant) {
    if (!variant) return;

    const hiddenIdInput = root.querySelector('[data-ck-variant-id]');
    const nativeSelect = root.querySelector('[data-ck-native-select]');
    const priceEl = root.querySelector('[data-ck-price]');
    const compareEl = root.querySelector('[data-ck-compare-price]');
    const discountEl = root.querySelector('[data-ck-discount-badge]');
    const stockEl = root.querySelector('[data-ck-stock-status]');
    const addBtn = root.querySelector('[data-ck-add-to-cart]');
    const addLabel = root.querySelector('[data-ck-add-label]');
    const buyBtn = root.querySelector('[data-ck-buy-now]');
    const stickyPrice = root.querySelector('[data-ck-sticky-price]');

    if (hiddenIdInput) hiddenIdInput.value = variant.id;
    if (nativeSelect) nativeSelect.value = variant.id;

    if (priceEl) priceEl.textContent = formatMoney(variant.price);
    if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      if (compareEl) {
        compareEl.textContent = formatMoney(variant.compare_at_price);
        compareEl.hidden = false;
      }
      if (discountEl) {
        const pct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
        discountEl.textContent = pct + '% OFF';
        discountEl.hidden = false;
      }
    } else {
      const fallbackPct = parseFloat(root.dataset.fallbackDiscount) || 0;
      if (fallbackPct > 0) {
        const syntheticCompareAt = Math.round(variant.price / (1 - fallbackPct / 100));
        if (compareEl) {
          compareEl.textContent = formatMoney(syntheticCompareAt);
          compareEl.hidden = false;
        }
        if (discountEl) {
          discountEl.textContent = fallbackPct + '% OFF';
          discountEl.hidden = false;
        }
      } else {
        if (compareEl) compareEl.hidden = true;
        if (discountEl) discountEl.hidden = true;
      }
    }

    if (stockEl) {
      stockEl.innerHTML = variant.available
        ? '<span class="ck-dot ck-dot--in"></span> In stock, ready to ship'
        : '<span class="ck-dot ck-dot--out"></span> Currently out of stock';
    }

    [addBtn, buyBtn].forEach((btn) => {
      if (!btn) return;
      btn.disabled = !variant.available;
    });
    if (addLabel) addLabel.textContent = variant.available ? 'Add to Cart' : 'Sold Out';

    // Swap the gallery to this variant's own image, if it has one.
    // root._ckVariantMediaMap (built directly in Liquid) gives us
    // {m: mediaId, f: filename} per variant, where mediaId comes
    // from variant.featured_media.id — the SAME id space product.media
    // uses (this is what Shopify's own Dawn/Horizon gallery relies on
    // internally). variant.featured_image.id lives in a different,
    // older id space and was the real reason matching kept failing.
    // Filename is kept as a secondary fallback. Variants with no
    // dedicated image (e.g. a Size-only variant on a product where
    // only Color has images) correctly leave the gallery untouched.
    if (typeof root._ckGallerySlideToMedia === 'function') {
      const mapEntry = root._ckVariantMediaMap && root._ckVariantMediaMap[String(variant.id)];
      const mappedMediaId = mapEntry && mapEntry.m;
      const mappedFilename = mapEntry && mapEntry.f;
      const fallbackId = variant.featured_image && variant.featured_image.id;
      const mediaId = mappedMediaId || fallbackId;
      if (mediaId || mappedFilename) {
        root._ckGallerySlideToMedia(mediaId, mappedFilename);
      }
    }
  }

  /* ============================================================
     VARIANT PICKER
     ============================================================ */
  function initVariantPicker(root, productData) {
    const optionSelectors = Array.from(root.querySelectorAll('[data-ck-option-selector]'));
    if (!optionSelectors.length) return;

    const variants = productData.variants || [];
    const selections = {};

    // seed current selections from the "is-selected" pill in each option group
    root.querySelectorAll('[data-ck-option]').forEach((group) => {
      const pos = group.dataset.optionPosition;
      const selected = group.querySelector('.is-selected');
      if (selected) selections[pos] = selected.dataset.value;
    });

    function findVariant() {
      return variants.find((v) => {
        const opts = [v.option1, v.option2, v.option3];
        return Object.keys(selections).every((pos) => {
          const idx = parseInt(pos, 10) - 1;
          return opts[idx] === selections[pos];
        });
      });
    }

    function updateUI(variant) {
      applyVariantToUI(root, variant);

      // update option value labels (specific to the pill picker)
      root.querySelectorAll('[data-ck-option]').forEach((group) => {
        const pos = group.dataset.optionPosition;
        const valueEl = group.querySelector('[data-ck-option-value]');
        if (valueEl && selections[pos]) valueEl.textContent = selections[pos];
      });
    }

    optionSelectors.forEach((pill) => {
      pill.addEventListener('click', () => {
        const group = pill.closest('[data-ck-option]');
        const pos = pill.dataset.optionPosition;

        group.querySelectorAll('[data-ck-option-selector]').forEach((p) => {
          p.classList.remove('is-selected');
          p.setAttribute('aria-checked', 'false');
        });
        pill.classList.add('is-selected');
        pill.setAttribute('aria-checked', 'true');

        selections[pos] = pill.dataset.value;
        const variant = findVariant();
        if (variant) updateUI(variant);
      });
    });
  }

  /* ============================================================
     VARIANT CARDS (photo + price) — the picker actually shown
     on the page. Full UI sync on click: price, compare price,
     discount badge, stock status, gallery image, and Add to
     Cart/Buy Now disabled state, not just the price text.
     ============================================================ */
  function initVariantCards(root, productData) {
    const wrap = root.querySelector('[data-ck-variant-cards]');
    if (!wrap) return;

    const variants = productData.variants || [];

    wrap.addEventListener('click', (e) => {
      const card = e.target.closest('[data-ck-variant-card]');
      if (!card || card.disabled) return;

      wrap.querySelectorAll('[data-ck-variant-card]').forEach((c) => {
        c.classList.remove('is-selected');
        c.setAttribute('aria-checked', 'false');
      });
      card.classList.add('is-selected');
      card.setAttribute('aria-checked', 'true');

      const variantId = card.dataset.variantId;
      const variant = variants.find((v) => String(v.id) === String(variantId));

      if (variant) {
        applyVariantToUI(root, variant);
      } else {
        // Fallback if productData wasn't available for some reason —
        // at least keep the variant id and price in sync, formatted
        // client-side (never from a pre-rendered money-filter string).
        const hiddenInput = root.querySelector('[data-ck-variant-id]');
        if (hiddenInput) hiddenInput.value = variantId;
        const priceEl = root.querySelector('[data-ck-price]');
        if (priceEl && card.dataset.variantPrice) {
          priceEl.textContent = formatMoney(parseInt(card.dataset.variantPrice, 10));
        }
      }
    });
  }

  /* ============================================================
     QUANTITY STEPPER
     ============================================================ */
  function initQuantityStepper(root) {
    const input = root.querySelector('[data-ck-qty-input]');
    const minus = root.querySelector('[data-ck-qty-minus]');
    const plus = root.querySelector('[data-ck-qty-plus]');
    if (!input) return;

    minus && minus.addEventListener('click', () => {
      const val = Math.max(1, (parseInt(input.value, 10) || 1) - 1);
      input.value = val;
    });
    plus && plus.addEventListener('click', () => {
      const val = (parseInt(input.value, 10) || 1) + 1;
      input.value = val;
    });
    input.addEventListener('change', () => {
      if (!input.value || parseInt(input.value, 10) < 1) input.value = 1;
    });
  }

  /* ============================================================
     SHARED TOAST — one small, self-contained confirmation banner
     used for cart feedback (and reusable anywhere else later).
     Appended to <body> once, reused for every call.
     ============================================================ */
  function ckShowToast(message, isError) {
    let toast = document.querySelector('.ck-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'ck-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.toggle('ck-toast--error', !!isError);

    // restart the animation/visibility cleanly even on rapid repeats
    toast.classList.remove('is-visible');
    // eslint-disable-next-line no-unused-expressions
    void toast.offsetWidth; // force reflow so the class re-add re-triggers CSS transition
    toast.classList.add('is-visible');

    window.clearTimeout(toast._ckHideTimer);
    toast._ckHideTimer = window.setTimeout(() => {
      toast.classList.remove('is-visible');
    }, 2600);
  }

  /* ============================================================
     OPEN CART DRAWER — triggers whichever cart UI the installed
     cart/checkout app (e.g. Shopflo) already provides, after a
     successful Add to Cart, instead of leaving the customer with
     only a toast.

     Primary method: Shopify's standard storefront actions API
     (window.Shopify.actions.openCart()) — the current, theme-
     and-app-agnostic way for any custom code to say "show the
     cart" without needing to know if that means a drawer, a
     popup, or a page. Modern cart apps are expected to configure
     this action to open their own UI.

     Fallback: if that API isn't present (older app version),
     click whatever cart-open trigger the site already has wired
     up — the same element a customer would click manually. This
     never fights with the primary method; only one runs per call.
     ============================================================ */
  function ckOpenCartDrawer() {
    try {
      if (window.Shopify && window.Shopify.actions && typeof window.Shopify.actions.openCart === 'function') {
        window.Shopify.actions.openCart();
        return;
      }
    } catch (e) {
      // fall through to the fallback below
    }

    var candidateSelectors = [
      '[data-cart-icon]',
      '[data-cart-drawer-toggle]',
      '#cart-icon-bubble',
      '.header__icon--cart',
      '.cart-icon',
      'a[href="/cart"]',
      'button[aria-controls*="cart" i]'
    ];
    for (var i = 0; i < candidateSelectors.length; i++) {
      var el = document.querySelector(candidateSelectors[i]);
      if (el) {
        el.click();
        return;
      }
    }
  }

  /* ============================================================
     GO TO CHECKOUT — used by Buy Now. Checkout accelerator apps
     like Shopflo work by injecting scripts that intercept clicks
     on the theme's REAL checkout button(s) and swap in their own
     accelerated flow. A raw `window.location.href = '/checkout'`
     is a scripted navigation, not a click — there's nothing for
     that injected script to intercept, so it lands on plain
     native Shopify checkout instead of Shopflo.

     Fix: open the cart (same trigger Shopflo already hooks for
     its "view cart → initiate checkout" flow), give it a moment
     to render, then look for a real Checkout button/link inside
     it and click that. Only if no such element can be found does
     this fall back to a direct /checkout navigation, so behavior
     never breaks — it just won't be Shopflo-accelerated in that
     edge case (e.g. Shopflo temporarily uninstalled).
     ============================================================ */
  function ckClickCheckoutButton() {
    var candidateSelectors = [
      'form[action^="/cart"] button[name="checkout"]',
      'button[name="checkout"]',
      '#CartDrawer-Checkout',
      '.cart__checkout-button',
      '[data-shopflo-checkout]',
      'a[href="/checkout"]'
    ];
    for (var i = 0; i < candidateSelectors.length; i++) {
      var el = document.querySelector(candidateSelectors[i]);
      if (el && el.offsetParent !== null) {
        el.click();
        return true;
      }
    }
    // Fallback: any visible button/link whose visible text is exactly
    // "Checkout" (catches Shopflo's own drawer button even without
    // knowing its exact class names in advance).
    var all = document.querySelectorAll('button, a');
    for (var j = 0; j < all.length; j++) {
      var text = (all[j].textContent || '').trim().toLowerCase();
      if (text === 'checkout' && all[j].offsetParent !== null) {
        all[j].click();
        return true;
      }
    }
    return false;
  }

  // FIX: a single check after a fixed 600ms delay was too fragile — on a
  // cold load, Shopflo's drawer can take noticeably longer than that to
  // render its Checkout button, so we were searching too early, finding
  // nothing, and falling back to plain native Shopify checkout even
  // though Shopflo genuinely was about to be ready. Polling repeatedly
  // for up to 4 seconds (instead of checking once) catches it whenever
  // it actually appears, however long that takes within a reasonable
  // ceiling, and still degrades gracefully to /checkout if it never
  // shows up at all.
  function ckGoToCheckout() {
    ckOpenCartDrawer();

    var maxWaitMs = 4000;
    var intervalMs = 150;
    var waited = 0;

    var poll = window.setInterval(() => {
      var clicked = ckClickCheckoutButton();
      waited += intervalMs;
      if (clicked || waited >= maxWaitMs) {
        window.clearInterval(poll);
        if (!clicked) {
          window.location.href = '/checkout';
        }
      }
    }, intervalMs);
  }

  /* ============================================================
     ADD TO CART / BUY NOW  (Shopify AJAX Cart API)
     Every trigger (main Add to Cart, main Buy Now, sticky Add,
     sticky Buy) now gives the customer clear feedback: the button
     shows "Adding…" while the request is in flight, is disabled
     to prevent accidental double-adds, then shows "Added ✓" (or
     "Redirecting…" for Buy Now) on success, plus a toast
     confirmation. On failure the button resets and an error toast
     explains what happened, instead of failing silently.
     ============================================================ */
  function initAddToCart(root) {
    const form = root.querySelector('#ck-product-form') || root.querySelector('.ck-form');
    const addBtn = root.querySelector('[data-ck-add-to-cart]');
    const buyBtn = root.querySelector('[data-ck-buy-now]');
    const stickyAddBtn = root.querySelector('[data-ck-sticky-add]');
    const stickyBuyBtn = root.querySelector('[data-ck-sticky-buy]');

    let isSubmitting = false;

    function getFormData() {
      const idInput = root.querySelector('[data-ck-variant-id]');
      const qtyInput = root.querySelector('[data-ck-qty-input]');
      return {
        id: idInput ? idInput.value : null,
        quantity: qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1
      };
    }

    // Captures each button's original inner markup once, so it can be
    // restored exactly (icons, arrow spans, etc.) after a loading state.
    function setBusy(btn, label) {
      if (!btn) return;
      if (btn.dataset.ckOriginalHtml === undefined) {
        btn.dataset.ckOriginalHtml = btn.innerHTML;
      }
      btn.disabled = true;
      btn.classList.add('is-loading');
      const textEl = btn.querySelector('.ck-btn-text') || btn.querySelector('[data-ck-add-label]');
      if (textEl) {
        textEl.textContent = label;
      } else {
        btn.textContent = label;
      }
    }

    function setDone(btn, label, revertAfterMs) {
      if (!btn) return;
      const textEl = btn.querySelector('.ck-btn-text') || btn.querySelector('[data-ck-add-label]');
      if (textEl) {
        textEl.textContent = label;
      } else {
        btn.textContent = label;
      }
      window.setTimeout(() => {
        resetButton(btn);
      }, revertAfterMs || 1600);
    }

    function resetButton(btn) {
      if (!btn) return;
      btn.disabled = false;
      btn.classList.remove('is-loading');
      if (btn.dataset.ckOriginalHtml !== undefined) {
        btn.innerHTML = btn.dataset.ckOriginalHtml;
      }
    }

    // FIX: previously this set the SAME label ("Adding…" / "Redirecting…")
    // on ALL FOUR buttons regardless of which one was actually clicked —
    // so clicking Buy Now made the Add to Cart button falsely say
    // "Redirecting…" too. Now each action only touches its own matching
    // pair (Add to Cart + sticky Add, or Buy Now + sticky Buy). The other
    // pair is left completely alone — the isSubmitting flag below already
    // prevents any real duplicate network request if a customer taps a
    // different button in the brief in-flight window, so there's no need
    // to visually disable buttons that aren't part of this action.
    function setActionBusy(redirectToCheckout, label) {
      if (redirectToCheckout) {
        setBusy(buyBtn, label);
        setBusy(stickyBuyBtn, label);
      } else {
        setBusy(addBtn, label);
        setBusy(stickyAddBtn, label);
      }
    }

    function resetActionButtons(redirectToCheckout) {
      if (redirectToCheckout) {
        resetButton(buyBtn);
        resetButton(stickyBuyBtn);
      } else {
        resetButton(addBtn);
        resetButton(stickyAddBtn);
      }
    }

    function addToCart(redirectToCheckout, triggerBtn) {
      if (isSubmitting) return; // guards against double taps mid-request
      const data = getFormData();
      if (!data.id) return;

      isSubmitting = true;
      setActionBusy(redirectToCheckout, redirectToCheckout ? 'Redirecting…' : 'Adding…');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      })
        .then((res) => {
          if (!res.ok) throw new Error('Add to cart failed');
          return res.json();
        })
        .then(() => {
          if (redirectToCheckout) {
            // Keep the Buy Now buttons in their "Redirecting…" state — the
            // page is about to navigate away, so there's nothing to reset
            // to. ckGoToCheckout (not a raw location.href) is what lets
            // this route through Shopflo's accelerated checkout instead
            // of landing on plain native Shopify checkout.
            ckGoToCheckout();
          } else {
            isSubmitting = false;
            [addBtn, stickyAddBtn].forEach((b) => setDone(b, 'Added ✓'));
            ckShowToast('Added to cart');
            ckOpenCartDrawer();
            document.dispatchEvent(new CustomEvent('ck:cart:added', { detail: data }));
            document.dispatchEvent(new CustomEvent('cart:refresh'));
          }
        })
        .catch(() => {
          isSubmitting = false;
          resetActionButtons(redirectToCheckout);
          ckShowToast("Couldn't add to cart. Please try again.", true);
          // Fallback: submit the native form so Shopify handles it server-side
          if (!redirectToCheckout && form) form.submit();
        });
    }

    // The form submit listener stays as a safety net (covers cases where
    // the button genuinely is inside the form, or Enter is pressed in a
    // field), but the DIRECT click listener below is what actually makes
    // "Add to Cart" reliable: this button is type="submit" but sits
    // outside <form id="ck-product-form"> in the rendered markup, and a
    // submit button with no form ancestor (and no form="..." attribute)
    // does nothing when clicked — no request, no error, just silence.
    // Buy Now / sticky Add / sticky Buy never had this problem because
    // they were already wired with direct click listeners.
    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        addToCart(false, addBtn);
      });
    }

    if (addBtn) {
      addBtn.addEventListener('click', (e) => {
        e.preventDefault();
        addToCart(false, addBtn);
      });
    }

    if (buyBtn) buyBtn.addEventListener('click', () => addToCart(true, buyBtn));
    if (stickyAddBtn) stickyAddBtn.addEventListener('click', () => addToCart(false, stickyAddBtn));
    if (stickyBuyBtn) stickyBuyBtn.addEventListener('click', () => addToCart(true, stickyBuyBtn));

    // FIX: pressing the browser's Back button after Buy Now navigated to
    // checkout can restore this page from the back-forward cache (bfcache)
    // instead of reloading it — meaning our JS does NOT re-run, and the
    // Buy Now button stays permanently stuck showing "Redirecting…" from
    // right before the navigation away. The pageshow event's `persisted`
    // flag is exactly how a bfcache restore is detected; when it fires,
    // put every cart button back to its normal, clickable state.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        isSubmitting = false;
        [addBtn, buyBtn, stickyAddBtn, stickyBuyBtn].forEach((b) => resetButton(b));
      }
    });
  }

  /* ============================================================
     STICKY ADD TO CART BAR — Intersection Observer, no scroll spam
     ============================================================ */
  function initStickyBar(root) {
    const bar = root.querySelector('[data-ck-sticky-bar]');
    const hero = root.querySelector('.ck-hero');
    if (!bar || !hero) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        bar.hidden = false;
        bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
    }, { threshold: 0, rootMargin: '0px' });

    observer.observe(hero);
  }

  /* ============================================================
     TABS
     ============================================================ */
  function initTabs(root) {
    const nav = root.querySelector('[data-ck-tabs-nav]');
    if (!nav) return;

    const buttons = Array.from(nav.querySelectorAll('[data-ck-tab-btn]'));
    const panels = Array.from(root.querySelectorAll('[data-ck-tab-panel]'));

    buttons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const target = btn.dataset.ckTabBtn;

        buttons.forEach((b) => b.classList.toggle('is-active', b === btn));
        panels.forEach((p) => p.classList.toggle('is-active', p.dataset.ckTabPanel === target));
      });
    });
  }


function initMobileAccordion(root) {

    // Desktop: do nothing
    if (window.innerWidth > 749) return;

    const items = root.querySelectorAll(".ck-mobile-item");

    if (!items.length) return;

    items.forEach((item) => {

        const button = item.querySelector(".ck-mobile-btn");

        if (!button) return;

        button.addEventListener("click", function () {

            const isOpen = item.classList.contains("active");

            items.forEach(i => i.classList.remove("active"));

            if (!isOpen) {
                item.classList.add("active");
            }

        });

    });

}

  /* ============================================================
     FAQ ACCORDION — only one open at a time
     ============================================================ */
  function initFaq(root) {
    const items = Array.from(root.querySelectorAll('[data-ck-faq-item]'));
    if (!items.length) return;

    items.forEach((item) => {
      const question = item.querySelector('[data-ck-faq-question]');
      const answer = item.querySelector('[data-ck-faq-answer]');
      if (!question || !answer) return;

      question.addEventListener('click', () => {
        const isOpen = item.classList.contains('is-open');

        items.forEach((other) => {
          other.classList.remove('is-open');
          const otherAnswer = other.querySelector('[data-ck-faq-answer]');
          if (otherAnswer) otherAnswer.style.maxHeight = null;
          const otherQ = other.querySelector('[data-ck-faq-question]');
          if (otherQ) otherQ.setAttribute('aria-expanded', 'false');
        });

        if (!isOpen) {
          item.classList.add('is-open');
          answer.style.maxHeight = answer.scrollHeight + 'px';
          question.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }


  /* ============================================================
     DELIVERY PINCODE CHECKER — custom delivery days per pincode,
     driven by a merchant-editable JSON prefix map in the schema
     (section.settings.pincode_delivery_map, rendered into a
     <script type="application/json" data-ck-pincode-map> tag by
     the .liquid file). Longest matching prefix wins (checks
     6-digit down to 1-digit), falling back to
     section.settings.default_delivery_days when nothing matches.
     ============================================================ */
  function initPincodeChecker(root) {
    const wrap = root.querySelector('.ck-delivery-checker');
    const btn = root.querySelector('[data-ck-check-pincode]');
    const input = root.querySelector('[data-ck-pincode]');
    const result = root.querySelector('[data-ck-delivery-result]');
    if (!btn || !input || !result) return;

    const defaultDays = parseInt((wrap && wrap.dataset.defaultDays), 10) || 5;

    let pincodeMap = {};
    const mapScript = root.querySelector('[data-ck-pincode-map]');
    if (mapScript) {
      try {
        pincodeMap = JSON.parse(mapScript.textContent.trim() || '{}');
      } catch (e) {
        pincodeMap = {};
      }
    }

    function getDeliveryDays(pincode) {
      // Check longest prefix first so more specific entries
      // (e.g. "400" for Mumbai) win over broader ones (e.g. "4").
      for (let len = 6; len >= 1; len--) {
        const prefix = pincode.slice(0, len);
        if (Object.prototype.hasOwnProperty.call(pincodeMap, prefix)) {
          return pincodeMap[prefix];
        }
      }
      return defaultDays;
    }

    function formatEta(days) {
      const date = new Date();
      date.setDate(date.getDate() + days);
      return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    }

    function checkPincode() {
      const val = input.value.trim();

      if (!/^\d{6}$/.test(val)) {
        result.hidden = false;
        result.classList.add('is-error');
        result.textContent = 'Please enter a valid 6-digit pincode.';
        return;
      }

      const days = getDeliveryDays(val);
      const eta = formatEta(days);

      result.hidden = false;
      result.classList.remove('is-error');
      result.textContent = days <= 1
        ? 'Delivery by tomorrow, ' + eta
        : 'Delivery in ' + days + ' days, by ' + eta;
    }

    btn.addEventListener('click', checkPincode);

    // keep the field numeric-only, max 6 digits, as the user types
    input.addEventListener('input', () => {
      input.value = input.value.replace(/\D/g, '').slice(0, 6);
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        checkPincode();
      }
    });
  }

  /* ============================================================
     BUTTON RIPPLE EFFECT
     ============================================================ */
  function initRipple(root) {
    root.querySelectorAll('.ck-ripple').forEach((btn) => {
      btn.addEventListener('click', function (e) {
        const rect = btn.getBoundingClientRect();
        const circle = document.createElement('span');
        const size = Math.max(rect.width, rect.height);
        circle.className = 'ck-ripple-circle';
        circle.style.width = circle.style.height = size + 'px';
        circle.style.left = (e.clientX - rect.left - size / 2) + 'px';
        circle.style.top = (e.clientY - rect.top - size / 2) + 'px';
        btn.appendChild(circle);
        window.setTimeout(() => circle.remove(), 650);
      });
    });
  }

  /* ============================================================
     FADE-IN ON SCROLL — single shared IntersectionObserver
     ============================================================ */
  function initFadeInObserver(root) {
    const targets = root.querySelectorAll('.ck-fade-in');
    if (!targets.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });

    targets.forEach((t) => observer.observe(t));

  }

  /* ============================================================
     RELATED / RECENTLY VIEWED SLIDER CONTROLS
     ============================================================ */
  function initRelatedSliders(root) {
    root.querySelectorAll('[data-ck-slider]').forEach((slider) => {
      const track = slider.querySelector('.ck-slider-track');
      const prev = slider.parentElement.querySelector('[data-ck-slider-prev]');
      const next = slider.parentElement.querySelector('[data-ck-slider-next]');
      if (!track) return;

      function scrollByAmount(dir) {
        const card = track.querySelector('.ck-product-card');
        const amount = card ? card.getBoundingClientRect().width + 20 : 280;
        track.scrollBy({ left: dir * amount, behavior: 'smooth' });
      }

      if (prev) prev.addEventListener('click', () => scrollByAmount(-1));
      if (next) next.addEventListener('click', () => scrollByAmount(1));
    });

    // Quick add buttons on product cards
    root.querySelectorAll('[data-ck-quick-add]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const variantId = btn.dataset.variantId;
        if (!variantId) return;

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        })
          .then((res) => res.json())
          .then(() => {
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            ckOpenCartDrawer();
            const label = btn.textContent;
            btn.textContent = 'Added ✓';
            window.setTimeout(() => { btn.textContent = label; }, 1600);
          })
          .catch(() => {});
      });
    });

    // Wishlist buttons on product cards
    root.querySelectorAll('[data-ck-card-wishlist]').forEach((btn) => {
      btn.addEventListener('click', () => {
        btn.classList.toggle('is-active');
      });
    });
  }

  /* ============================================================
     CURRENCY FORMAT HELPER — shared by Recently Viewed + FBT
     ============================================================ */
  function formatCurrency(amount) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
        style: 'currency',
        currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'INR',
        maximumFractionDigits: 0
      }).format(amount);
    } catch (e) {
      return '₹' + Math.round(amount);
    }
  }

  /* ============================================================
     RECENTLY VIEWED — localStorage-backed, matches the
     Related Products card style (.ck-rv-*), with a working
     persistent Add to Cart button per card.
     ============================================================ */
  function initRecentlyViewed(root, productData) {
    const key = 'ck_recently_viewed';
    const handle = root.dataset.productHandle;
    if (!handle) return;

    let list = [];
    try { list = JSON.parse(localStorage.getItem(key) || '[]'); } catch (e) { list = []; }

    list = list.filter((h) => h !== handle);
    list.unshift(handle);
    list = list.slice(0, 12);

    try { localStorage.setItem(key, JSON.stringify(list)); } catch (e) { /* storage unavailable */ }

    const section = root.querySelector('[data-ck-recently-viewed]');
    const track = root.querySelector('[data-ck-recently-viewed-track]');
    const others = list.filter((h) => h !== handle).slice(0, 8);

    if (!section || !track || others.length === 0) return;

    const run = window.requestIdleCallback || window.setTimeout;
    run(() => {
      Promise.all(
        others.map((h) =>
          fetch('/products/' + h + '.js')
            .then((res) => (res.ok ? res.json() : null))
            .catch(() => null)
        )
      ).then((products) => {
        products.filter(Boolean).forEach((p) => {
          track.appendChild(buildRecentlyViewedCard(p));
        });
        if (track.children.length) section.hidden = false;
      });
    });

    // Delegated add-to-cart for the persistent button on each card
    track.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-ck-rv-add]');
      if (!btn) return;
      const variantId = btn.dataset.variantId;
      if (!variantId) return;
      e.preventDefault();

      const original = btn.textContent;
      btn.textContent = 'Adding…';
      btn.disabled = true;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      })
        .then((res) => res.json())
        .then(() => {
          btn.textContent = 'Added ✓';
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          ckOpenCartDrawer();
          window.setTimeout(() => {
            btn.textContent = original;
            btn.disabled = false;
          }, 1600);
        })
        .catch(() => {
          btn.textContent = original;
          btn.disabled = false;
        });
    });
  }

  function buildRecentlyViewedCard(product) {
    const rawImg = product.featured_image || (product.images && product.images[0]) || '';
    const img = rawImg ? (rawImg.indexOf('http') === 0 ? rawImg : 'https:' + rawImg) : '';

    const price = product.price / 100;
    const compareAt = product.compare_at_price ? product.compare_at_price / 100 : null;
    const hasDiscount = compareAt && compareAt > price;
    const savePct = hasDiscount ? Math.round(((compareAt - price) / compareAt) * 100) : 0;

    let variant = (product.variants || []).find((v) => v.available);
    if (!variant && product.variants && product.variants.length) variant = product.variants[0];

    const card = document.createElement('div');
    card.className = 'ck-rv-card';

    const media = document.createElement('div');
    media.className = 'ck-rv-media';

    if (hasDiscount) {
      const badge = document.createElement('span');
      badge.className = 'ck-rv-badge';
      badge.textContent = savePct + '% OFF';
      media.appendChild(badge);
    }

    const wishlistBtn = document.createElement('button');
    wishlistBtn.type = 'button';
    wishlistBtn.className = 'ck-rv-wishlist';
    wishlistBtn.setAttribute('data-ck-card-wishlist', '');
    wishlistBtn.setAttribute('aria-label', 'Add to wishlist');
    wishlistBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 21s-7.5-4.6-10-9.2C.4 8.1 2.1 4 6 4c2.1 0 3.6 1.1 4.5 2.4C11.4 5.1 12.9 4 15 4c3.9 0 5.6 4.1 4 7.8-2.5 4.6-10 9.2-10 9.2Z"/></svg>';
    media.appendChild(wishlistBtn);

    const mediaLink = document.createElement('a');
    mediaLink.href = '/products/' + product.handle;
    mediaLink.className = 'ck-rv-media-link';
    if (img) {
      const imgEl = document.createElement('img');
      imgEl.src = img;
      imgEl.alt = product.title || '';
      imgEl.loading = 'lazy';
      imgEl.width = 260;
      imgEl.height = 260;
      mediaLink.appendChild(imgEl);
    }
    media.appendChild(mediaLink);
    card.appendChild(media);

    const body = document.createElement('div');
    body.className = 'ck-rv-body';

    const titleLink = document.createElement('a');
    titleLink.href = '/products/' + product.handle;
    titleLink.className = 'ck-rv-title';
    titleLink.textContent = product.title;
    body.appendChild(titleLink);

    const priceRow = document.createElement('div');
    priceRow.className = 'ck-rv-price-row';
    const priceNow = document.createElement('span');
    priceNow.className = 'ck-rv-price-now';
    priceNow.textContent = formatCurrency(price);
    priceRow.appendChild(priceNow);
    if (hasDiscount) {
      const priceWas = document.createElement('span');
      priceWas.className = 'ck-rv-price-was';
      priceWas.textContent = formatCurrency(compareAt);
      priceRow.appendChild(priceWas);
    }
    body.appendChild(priceRow);

    const addBtn = document.createElement('button');
    addBtn.type = 'button';
    addBtn.className = 'ck-rv-add';
    if (variant && variant.available) {
      addBtn.textContent = 'Add to Cart';
      addBtn.setAttribute('data-ck-rv-add', '');
      addBtn.dataset.variantId = variant.id;
    } else {
      addBtn.textContent = 'View Product';
      addBtn.addEventListener('click', () => {
        window.location.href = '/products/' + product.handle;
      });
    }
    body.appendChild(addBtn);

    card.appendChild(body);
    return card;
  }

  /* ============================================================
     FREQUENTLY BOUGHT TOGETHER — live total + single multi-item
     add to cart. Anchor product is always included.
     ============================================================ */
  function initFrequentlyBoughtTogether(root) {
    const fbt = root.querySelector('[data-ck-fbt]');
    if (!fbt) return;

    const checkboxes = Array.from(fbt.querySelectorAll('[data-ck-fbt-checkbox]'));
    const totalEl = fbt.querySelector('[data-ck-fbt-total]');
    const countEl = fbt.querySelector('[data-ck-fbt-item-count]');
    const addBtn = fbt.querySelector('[data-ck-fbt-add]');

    function recalc() {
      let total = 0;
      let count = 0;
      checkboxes.forEach((cb) => {
        if (cb.checked) {
          const item = cb.closest('[data-ck-fbt-item]');
          total += parseFloat(item.dataset.price) || 0;
          count++;
        }
      });
      if (totalEl) totalEl.textContent = formatCurrency(total / 100);
      if (countEl) countEl.textContent = count;
    }

    checkboxes.forEach((cb) => cb.addEventListener('change', recalc));

    if (addBtn) {
      addBtn.addEventListener('click', () => {
        const items = [];
        checkboxes.forEach((cb) => {
          if (!cb.checked) return;
          const variantId = cb.dataset.variantId;
          if (variantId) items.push({ id: variantId, quantity: 1 });
        });
        const anchorInput = root.querySelector('[data-ck-variant-id]');
        if (anchorInput && anchorInput.value) items.unshift({ id: anchorInput.value, quantity: 1 });
        if (!items.length) return;

        const original = addBtn.textContent;
        addBtn.disabled = true;
        addBtn.textContent = 'Adding…';

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items })
        })
          .then((res) => res.json())
          .then(() => {
            addBtn.textContent = 'Added ✓';
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            ckOpenCartDrawer();
            window.setTimeout(() => {
              addBtn.textContent = original;
              addBtn.disabled = false;
            }, 1600);
          })
          .catch(() => {
            addBtn.textContent = original;
            addBtn.disabled = false;
          });
      });
    }

    recalc();
  }
})();
